// Pure-TS store backing the SnapshotContext (`./SnapshotContext.tsx`).
// Lives in its own file so the React-free logic (ref-counting, subscribe-
// first ordering, version guard, buffer-drain) can be unit-tested in node
// without rendering a tree. The React wrapper imports from here.
//
// ADR 011 §5 broadcast protocol (amended 2026-06-09):
//   • Each topic backed by an `<x>_snapshot` table + per-app catch-up
//     route mounted at `/api/realtime/<x>_snapshot/catchup` (per the
//     resolver below).
//   • The store subscribes to TWO event families per topic — a topic
//     emits ONE based on its data-sensitivity:
//       1. `snapshot.updated` (payload-carrying, PR-D5fix and forward) —
//          carries `{ topicId, version, snapshot }`. The
//          store applies the payload directly when the version is the
//          next-expected one (or first-apply); only fires catch-up on
//          a detected version gap. Used for **public-data** snapshots
//          whose projection is safe on the anon-key Realtime transport.
//       2. `snapshot.updated.hint` (payload-less, PR-D1b user-registrations
//          + future privileged-data domains) — carries only
//          `{ topicId, version }`. Every hint fires an HTTP catch-up; the
//          round-trip is the authorization gate for projections that
//          include privileged fields (e.g. paddleNumber).
//   • Initial mount: every NEW subscriber's first subscribe runs a
//     catch-up at `since_version = entry.version` (0 if no
//     `initialVersion` hydration prop). The catch-up returns either an
//     updated snapshot or `304 Not Modified`. Same code path for both
//     event families — the broadcast type only affects steady-state.
//   • Per-topic monotonic `version` is the canonical ordering cursor.
//     Dedup of duplicate broadcasts (storm, reconnect replay), gap
//     detection across missed broadcasts, and out-of-order rejection
//     all key off it.
//
// Transitional shim: subscribers can still hand `useSnapshot` an
// `initialFetch` callback for backwards-compat with the pre-PR-D1b
// signature. It's logged + ignored in dev mode and silently dropped
// otherwise — per-domain consumers (the canonical case: `useBidRegistration`)
// drop the arg in their own PR-Dn.

import {
    applyResponseStatusHandlers,
    type ResponseStatusHandlers,
} from '../../api/response-status-handlers';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import { redactTopicSecret } from '../../isomorphic/realtime/topics';
import { parseSnapshotTopic } from './parse-snapshot-topic';
import type { Unsubscribe } from '../../types/realtime/types';
import { formatRealtimeFailure, httpStatusOf } from '../../isomorphic/realtime/realtime-failure-log';

/**
 * Cap on `bufferedHints[]` per topic. During a broadcast storm (the
 * same publisher firing thousands of hints while an in-flight fetch
 * settles), only the maximum buffered version matters for the
 * re-fetch decision — older entries are redundant. Cap at 32 so the
 * array can't grow unboundedly while still absorbing realistic storm
 * windows. Hints dropped past the cap are semantically safe: the
 * highest-version hint always wins, and any dropped hint that was
 * lower-numbered would have been redundant anyway.
 */
const MAX_BUFFERED_HINTS = 32;

/**
 * Catch-up response from the per-app trampoline route. Matches the ADR
 * 011 §4 envelope shape returned by
 * `@auctogon/realtime/catchup/snapshot-catchup-factory`. Consumer hooks
 * never see this directly — the store deserialises and applies under
 * `applyIfNewer`.
 */
export interface SnapshotCatchupResult {
    /** The full current-state projection. */
    snapshot: unknown;
    /** Per-topic monotonic version cursor. */
    version: number;
}

/**
 * A recorded catch-up failure for a topic (BUG-1015). Only set when the
 * catch-up received an HTTP response with a non-2xx status (server reached,
 * refused) — the "show an error / Try again" case. A no-response / network
 * reject (offline, DNS) does NOT produce one: the store keeps the topic in
 * loading so it self-recovers on the next `online` / reconcile tick. Exposed
 * via `SnapshotStore.getError` so consumers can leave the loading branch and
 * render a bounded error instead of spinning forever.
 */
export interface SnapshotFetchError {
    /** Human-readable failure summary (from the thrown error's message). */
    message: string;
    /** The non-2xx HTTP status that caused the failure. */
    status: number;
}

/**
 * Thrown by the default catch-up fetcher on a non-2xx HTTP response so the
 * store can distinguish an HTTP failure (carries `status`) from a network
 * reject (no `status`). Exported so a later PR's 418/401 routing on the
 * catch-up path can key off `status` without re-parsing the message.
 */
export class SnapshotCatchupError extends Error {
    constructor(
        readonly status: number,
        message: string,
    ) {
        super(message);
        this.name = 'SnapshotCatchupError';
    }
}

/**
 * Fetcher seam: given a topic + the consumer's current version, hit the
 * per-app catch-up route. Returning `null` means HTTP 304 — no update.
 *
 * Apps inject this via `SnapshotProvider` (the default fetcher calls
 * `/api/realtime/<kind>/catchup?topic_id=<id>&since_version=<n>` with
 * `fetch`).
 */
export type SnapshotCatchupFetcher = (
    topic: string,
    sinceVersion: number,
) => Promise<SnapshotCatchupResult | null>;

/**
 * Backwards-compat shim type. Domain hooks still calling the old
 * signature pass this; the store warns once in dev mode and ignores it.
 * Drops out of the public surface in the cleanup PR after every domain
 * migrates.
 */
export type LegacySnapshotFetcher<T = unknown> = () => Promise<{
    snapshot: T;
}>;

/**
 * Optional initial-state hydration props. SSR or a parent page that
 * already has the catch-up result can avoid the post-mount round-trip by
 * passing the latest cursor + snapshot data here. The store treats this
 * as an authoritative seed for the FIRST subscriber; subsequent
 * subscribers piggyback on the existing entry as before.
 */
export interface SnapshotInitialHydration {
    /**
     * The version cursor at hydration time. Used as the floor for both
     * the post-mount catch-up's `since_version` and the per-topic
     * monotonic guard against stale hint broadcasts.
     */
    initialVersion?: number;
    /** The snapshot value at hydration time. */
    initialSnapshot?: unknown;
}

interface SnapshotEntry {
    snapshot: unknown;
    /**
     * Per-topic monotonic version cursor. 0 on first subscribe (the
     * post-mount catch-up returns the row's actual version, or stays
     * at 0 for users without a row); incremented as hints arrive.
     */
    version: number;
    subscribers: number;
    transportUnsubscribe: Unsubscribe | undefined;
    /**
     * In-flight catch-up: when true, hint broadcasts are buffered for
     * apply-after-fetch ordering (same buffer-drain pattern the
     * legacy initial-fetch code path used).
     */
    fetching: boolean;
    /**
     * Hint versions received while `fetching` is true. After the
     * catch-up settles the store re-evaluates each and re-fetches if
     * any is newer than the just-applied version.
     */
    bufferedHints: number[];
    /**
     * The last recorded HTTP-status catch-up failure, or `undefined` when the
     * topic is healthy / still loading. Set in `runCatchup`'s catch on a
     * non-2xx response; cleared on the next successful settle (result or 304).
     * Its presence is what lets a consumer distinguish "failed permanently"
     * from "not loaded yet" (BUG-1015).
     */
    error: SnapshotFetchError | undefined;
    listeners: Set<() => void>;
}

/**
 * Pure-TS snapshot store. Owns one `SnapshotEntry` per active topic
 * with ref-counted shared subscriptions: the first subscriber opens
 * the transport channel and (optionally) runs the post-mount catch-up;
 * the last subscriber to leave closes the channel and drops the entry.
 *
 * @sideEffect Opens transport subscriptions; invokes the supplied
 *   catch-up fetcher; logs failed fetches / unexpected envelope shapes
 *   via console.* (does not throw).
 */
export class SnapshotStore {
    private readonly entries = new Map<string, SnapshotEntry>();
    private legacyFetcherWarned = false;

    constructor(
        private readonly subscriber: RealtimeSubscriber,
        private readonly catchupFetcher: SnapshotCatchupFetcher,
    ) {}

    /**
     * Attach a listener for `topic`. The first subscriber opens the
     * transport channel and (if no hydration is supplied) runs the
     * post-mount catch-up.
     *
     * Returns an idempotent Unsubscribe that decrements the ref count
     * and closes the underlying channel when the last subscriber
     * leaves.
     */
    subscribe(
        topic: string,
        listener: () => void,
        opts?: SnapshotInitialHydration & {
            /** Backwards-compat: ignored, warned in dev. */
            legacyInitialFetch?: LegacySnapshotFetcher;
        },
    ): Unsubscribe {
        // BL-514: short-circuit when the caller passes an empty topic. This
        // is the standard "no subscription yet — we don't have an id" sentinel
        // (e.g. `useRealtimeLot(null)` → `topic = ''`). Without this guard we
        // would open a transport subscription for an unparseable topic name
        // and the catch-up fetcher would throw inside `parseSnapshotTopic('')`,
        // logging a spurious error on every consumer that hasn't loaded an
        // id yet. Listeners attached to '' never fire because no broadcast
        // ever targets that topic — the no-op unsubscribe is the right
        // behaviour for the hooks-before-conditionals call shape.
        if (!topic) {
            return () => {};
        }
        let entry = this.entries.get(topic);
        if (!entry) {
            entry = {
                snapshot: opts?.initialSnapshot,
                version: opts?.initialVersion ?? 0,
                subscribers: 0,
                transportUnsubscribe: undefined,
                fetching: false,
                bufferedHints: [],
                error: undefined,
                listeners: new Set(),
            };
            this.entries.set(topic, entry);
        }

        const isFirstSubscriber = entry.subscribers === 0;
        entry.listeners.add(listener);
        entry.subscribers++;

        // Warn once per process that a domain hook is still on the
        // legacy `initialFetch` signature. The post-cleanup PR deletes
        // both this shim and the warning.
        if (opts?.legacyInitialFetch && !this.legacyFetcherWarned) {
            this.legacyFetcherWarned = true;
            // eslint-disable-next-line no-console
            console.warn(
                '[SnapshotContext] useSnapshot was called with an ' +
                    'initialFetch argument; the catch-up endpoint now ' +
                    'owns initial state. Drop the arg in this domain hook.',
            );
        }

        if (isFirstSubscriber) {
            this.openTransportSubscription(topic);
            // Always run a post-mount catch-up so the FIRST subscriber
            // either seeds from the table (no hydration) or confirms
            // the hydration is current (hydration matches → 304).
            void this.runCatchup(topic);
        }

        let disposed = false;
        return () => {
            if (disposed) return;
            disposed = true;
            const e = this.entries.get(topic);
            if (!e) return;
            e.listeners.delete(listener);
            e.subscribers--;
            if (e.subscribers === 0) {
                e.transportUnsubscribe?.();
                this.entries.delete(topic);
            }
        };
    }

    /**
     * Current snapshot value for `topic`, or `undefined` if no value
     * yet. The returned reference is stable between updates — `useMemo`
     * over this value behaves as expected.
     */
    getSnapshot(topic: string): unknown {
        return this.entries.get(topic)?.snapshot;
    }

    /**
     * Current version cursor for `topic`. Exposed for test/diagnostic
     * inspection; downstream consumers should not branch on this.
     */
    getVersion(topic: string): number {
        return this.entries.get(topic)?.version ?? 0;
    }

    /**
     * The last recorded HTTP catch-up failure for `topic`, or `undefined` when
     * the topic is healthy / still loading. Consumers branch on this to render
     * a bounded error + retry instead of an indefinite spinner (BUG-1015).
     * Stable reference between changes — safe as a `useSyncExternalStore`
     * snapshot getter.
     */
    getError(topic: string): SnapshotFetchError | undefined {
        return this.entries.get(topic)?.error;
    }

    /**
     * Manually re-run the catch-up for `topic` — the "Try again" affordance
     * behind a surfaced error. No-op when the topic is unknown or a catch-up
     * is already in flight (the in-flight settle covers the retry). On success
     * the error clears and subscribers re-render; a fresh failure re-sets it.
     *
     * Resolves after the catch-up settles (`runCatchup` swallows its own
     * failures, so this never rejects) — the "Try again" button awaits it to
     * span its in-button spinner over the real round-trip. Resolves
     * immediately when skipped.
     *
     * @sideEffect Fires an async HTTP catch-up (via `runCatchup`).
     */
    async retry(topic: string): Promise<void> {
        const entry = this.entries.get(topic);
        if (!entry || entry.fetching) return;
        await this.runCatchup(topic);
    }

    /**
     * Test-only inspection: number of active subscribers for `topic`.
     */
    subscriberCount(topic: string): number {
        return this.entries.get(topic)?.subscribers ?? 0;
    }

    /**
     * Fire a catch-up against every active topic. SnapshotProvider mounts
     * call this on focus / visibilitychange / online / transport reconnect
     * / 2-min interval (per ADR 011 §5 / ADR 017 §5) so any broadcasts
     * missed while the tab was idle, asleep, or disconnected are pulled in
     * via the per-topic catch-up endpoint.
     *
     * Skips topics with a catch-up already in flight — mirrors
     * `LedgerStore.reconcileAll`'s `reconciling` guard. Prevents a wake-
     * trigger burst (focus + visibilitychange + online all firing within
     * the same second) from producing N HTTP requests per topic. The
     * single in-flight fetch's hint-buffer drain still catches anything
     * that arrived during its window.
     *
     * @sideEffect Fires an async HTTP catch-up request (via `runCatchup`)
     *   per active, non-fetching topic. Logs failed fetches via
     *   `console.error`. Does not throw.
     */
    reconcileAll(): void {
        for (const [topic, entry] of this.entries) {
            if (entry.fetching) continue;
            void this.runCatchup(topic);
        }
    }

    private openTransportSubscription(topic: string): void {
        const entry = this.entries.get(topic);
        if (!entry) return;
        // Subscribe to BOTH snapshot event families on this topic. Per
        // ADR 011 §5 (amended 2026-06-09) a topic emits one family based
        // on its data-sensitivity; the other subscription is dormant
        // for that topic. Composing both Unsubscribes into one preserves
        // the single-`transportUnsubscribe` invariant downstream.
        const updateUnsub = this.subscriber.subscribe({
            topic,
            event: 'snapshot.updated',
            handler: (payload) => {
                this.handleUpdate(topic, payload);
            },
        });
        const hintUnsub = this.subscriber.subscribe({
            topic,
            event: 'snapshot.updated.hint',
            handler: (payload) => {
                this.handleHint(topic, payload);
            },
        });

        entry.transportUnsubscribe = () => {
            updateUnsub();
            hintUnsub();
        };
    }

    /**
     * Payload-carrying handler. The broadcast IS the update — apply
     * directly on the happy path (no HTTP). Catch-up fires only on a
     * detected version gap, which signals at least one earlier broadcast
     * was missed.
     */
    private handleUpdate(topic: string, payload: unknown): void {
        const entry = this.entries.get(topic);
        if (!entry) return;
        const update = payload as {
            topicId: string;
            version: number;
            snapshot: unknown;
        };
        // Malformed-payload guard. Two required fields; a missing or
        // wrong-typed one means the publisher emitted a stale shape
        // (mid-rollout drift, future regression). Don't silently drop —
        // log a warn so the discard is visible.
        //
        // Observability surface: this store runs browser-side (Next.js
        // client component), so the warn lands in the browser console
        // (and in browser telemetry if/when that's wired up) — NOT in
        // server-side logs via the console monkeypatch (that only
        // runs in the Next.js server runtime). The authoritative
        // application-layer signal during a mid-rollout incident is the
        // upstream `[snapshot-publisher]` server log; this client warn
        // is a downstream confirmation operators can pull from bidder
        // devtools / RUM when triaging "the UI didn't update."
        //
        // `snapshot: undefined` is rejected too; without it
        // `applyIfNewer` would store `undefined` and notify subscribers
        // with no value, crashing consumers that dereference fields.
        if (
            typeof update.version !== 'number' ||
            update.snapshot === undefined
        ) {
            // eslint-disable-next-line no-console
            console.warn(
                '[SnapshotContext] handleUpdate dropped malformed payload',
                { topic, update },
            );
            return;
        }

        // Dedup: a broadcast at or below our current cursor is a
        // duplicate / out-of-order delivery (storm, reconnect replay).
        if (update.version <= entry.version) {
            return;
        }

        // If a catch-up is already in-flight, buffer the version and let
        // the drain logic in `runCatchup` re-evaluate. Applying the
        // payload mid-flight could race the catch-up result; the buffered
        // version triggers a re-fetch after settle if it's newer than
        // the just-applied cursor.
        if (entry.fetching) {
            if (entry.bufferedHints.length < MAX_BUFFERED_HINTS) {
                entry.bufferedHints.push(update.version);
            }
            return;
        }

        // First-apply OR next-expected version: apply payload directly.
        // No HTTP round-trip on the happy path — this is the win the
        // §5 amendment delivers over hint-only.
        const firstApply = entry.snapshot === undefined;
        if (firstApply || update.version === entry.version + 1) {
            this.applyIfNewer(topic, update.snapshot, update.version);
            return;
        }

        // Gap: `update.version > entry.version + 1`. We missed at least
        // one prior broadcast. Fire catch-up to pull the authoritative
        // current row; DON'T apply THIS payload — the catch-up may return
        // a version newer than this broadcast (storm in flight), and the
        // catch-up result is the canonical recovery.
        void this.runCatchup(topic);
    }

    private handleHint(topic: string, payload: unknown): void {
        const entry = this.entries.get(topic);
        if (!entry) return;
        const hint = payload as { topicId: string; version: number };
        if (typeof hint.version !== 'number') return;

        // Dedup: a hint at or below our current cursor is a duplicate
        // broadcast (storm, reconnect replay) — drop it.
        if (hint.version <= entry.version) {
            return;
        }

        // If a catch-up is already in-flight, buffer the hint version
        // so we can re-evaluate after the in-flight settles. Cap the
        // buffer at MAX_BUFFERED_HINTS — only the max version matters
        // at drain time, so dropping older entries past the cap is
        // semantically safe and bounds memory on a broadcast storm.
        if (entry.fetching) {
            if (entry.bufferedHints.length < MAX_BUFFERED_HINTS) {
                entry.bufferedHints.push(hint.version);
            }
            return;
        }

        void this.runCatchup(topic);
    }

    private async runCatchup(topic: string): Promise<void> {
        const entry = this.entries.get(topic);
        if (!entry) return;
        // BL-2342 (ADR 066 — Offline Mode § 6.2): no catch-up while the
        // transport is suspended for offline mode. The topic stays registered,
        // and the resume's `reconcileAll` catches it up.
        if (this.subscriber.isSuspended?.()) return;
        entry.fetching = true;
        try {
            const result = await this.catchupFetcher(topic, entry.version);
            // Identity check: if the entry was deleted (last subscriber
            // left) and a fresh one was created in its place, don't
            // seed it with this old fetcher's data.
            if (this.entries.get(topic) !== entry) return;

            if (result !== null) {
                this.applyIfNewer(topic, result.snapshot, result.version);
            }
            // 304 (result === null) is a no-op — our cursor matched.

            // A successful settle (applied result OR 304) means the topic is
            // healthy again — clear any recorded failure and wake consumers out
            // of the error branch. This is what makes the 2-min interval /
            // focus / online reconcile (and the manual retry) heal a prior
            // error (BUG-1015).
            this.clearError(entry);

            // Drain hint buffer: if any buffered hint is newer than
            // our (possibly just-updated) version, re-fetch.
            const buffered = entry.bufferedHints;
            entry.bufferedHints = [];
            entry.fetching = false;
            const maxBuffered = buffered.reduce(
                (m, v) => (v > m ? v : m),
                0,
            );
            if (maxBuffered > entry.version) {
                // Recursive call — the new fetch sees the just-updated
                // entry.version as its cursor.
                void this.runCatchup(topic);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(
                ...formatRealtimeFailure(
                    '[SnapshotContext] catch-up fetch failed',
                    { topic: redactTopicSecret(topic) },
                    err,
                ),
            );
            if (this.entries.get(topic) !== entry) return;

            // Classify the failure. An HTTP-status failure (server reached,
            // non-2xx — a 5xx, or a 4xx like the banned-customer 418) is
            // recorded as a distinguishable error + notified so consumers leave
            // the loading branch and render "Try again" instead of spinning
            // forever (BUG-1015). A no-response / network reject carries no
            // status: we DON'T record an error — the topic stays in loading and
            // self-recovers on the next `online` / reconcile tick, since a
            // "Try again" affordance is meaningless while the browser is
            // offline. A failure never blanks an existing snapshot.
            const status = httpStatusOf(err);
            if (status !== undefined) {
                entry.error = {
                    message:
                        err instanceof Error ? err.message : String(err),
                    status,
                };
            }

            // Drain the buffer + retry once if hints arrived during the failed
            // fetch: in a quiet auction with no follow-up hints, the consumer
            // would otherwise sit until an external event nudged it. Preserved
            // from the pre-BUG-1015 behavior (RAC 5) — the new error path must
            // not double-fire it.
            const buffered = entry.bufferedHints;
            entry.bufferedHints = [];
            entry.fetching = false;
            if (status !== undefined) {
                this.notify(entry);
            }
            if (buffered.length > 0) {
                void this.runCatchup(topic);
            }
        }
    }

    /**
     * Clear a recorded catch-up error and notify subscribers, if one was set.
     * No-op when the topic is already healthy so a routine successful settle
     * doesn't fire a spurious re-render.
     */
    private clearError(entry: SnapshotEntry): void {
        if (entry.error === undefined) return;
        entry.error = undefined;
        this.notify(entry);
    }

    /** Fire every listener attached to `entry`. */
    private notify(entry: SnapshotEntry): void {
        entry.listeners.forEach((listener) => {
            listener();
        });
    }

    private applyIfNewer(
        topic: string,
        snapshot: unknown,
        version: number,
    ): void {
        const entry = this.entries.get(topic);
        if (!entry) return;
        // First-apply: a brand-new entry with no snapshot lands the
        // value regardless of version. The catch-up for a zero-rows
        // user returns `version=0` (synthesized empty); without this
        // branch the store would never leave its initial-undefined
        // state for first-time bidders.
        const firstApply = entry.snapshot === undefined;
        // Per-topic monotonic version is the canonical (and only) cursor.
        // Equal-version events are dropped at handleUpdate / handleHint
        // before reaching here, so there's no equal-version branch.
        if (!firstApply && version <= entry.version) {
            return;
        }
        entry.snapshot = snapshot;
        entry.version = version;
        // Any successful application of a snapshot — via catch-up OR a direct
        // live `snapshot.updated` broadcast (the payload-carrying family, which
        // never routes through runCatchup on its happy path) — clears a
        // previously-recorded catch-up error, so a recovered topic never stays
        // stuck on the error branch (BUG-1015). The single notify below covers
        // the clear; runCatchup's own clearError still handles the 304 path
        // where no snapshot is applied.
        entry.error = undefined;
        this.notify(entry);
    }
}

/**
 * Default catch-up fetcher. Per-app SnapshotProviders can override the
 * `catchupBase` (path prefix) or supply a fully custom fetcher.
 *
 * @sideEffect Issues one GET to the resolved URL. Returns `null` on
 *   `304 Not Modified`. Throws (caught + logged by `runCatchup`) on
 *   any other non-2xx status or malformed envelope.
 */
export function defaultSnapshotCatchupFetcher(
    catchupBase = '/api/realtime',
    handlers?: ResponseStatusHandlers,
): SnapshotCatchupFetcher {
    return async (topic, sinceVersion) => {
        const parsed = parseSnapshotTopic(topic);
        if (!parsed) {
            throw new Error(
                `defaultSnapshotCatchupFetcher: cannot parse topic '${topic}'`,
            );
        }
        const url = new URL(
            `${catchupBase}/${parsed.kind}/catchup`,
            // Browsers always have window.location; SSR callers pass
            // an absolute catchupBase to skip this.
            typeof window !== 'undefined'
                ? window.location.origin
                : 'http://localhost',
        );
        url.searchParams.set('topic_id', parsed.topicId);
        url.searchParams.set('since_version', String(sinceVersion));
        for (const [name, value] of Object.entries(parsed.params ?? {})) {
            url.searchParams.set(name, value);
        }

        const res = await fetch(url.toString(), {
            method: 'GET',
            // Snapshot data is per-user / per-resource; never cache.
            cache: 'no-store',
            credentials: 'same-origin',
        });
        if (res.status === 304) return null;
        if (!res.ok) {
            // BUG-1015 AC 3: route a banned (418) / stale-session (401)
            // catch-up through the SAME app handlers the typed client uses,
            // BEFORE throwing — so a banned customer lands on the
            // account-unavailable screen instead of the store's generic error
            // state. The raw-fetch catch-up can't use the typed client
            // (packages/core can't import an app registry), so it shares only
            // the dispatch. A 5xx / other status has no handler → falls through
            // to the thrown error + the "Try again" UI.
            if (handlers) applyResponseStatusHandlers(res, handlers);
            // Typed so the store can tell an HTTP failure (has `status`) from a
            // network reject (the `fetch` `TypeError`, no status) — see
            // `SnapshotFetchError` / `httpStatusOf`.
            throw new SnapshotCatchupError(
                res.status,
                `snapshot catch-up failed: ${res.status} ${res.statusText}`,
            );
        }
        const body = (await res.json()) as {
            success: boolean;
            data?: SnapshotCatchupResult;
        };
        if (!body.success || !body.data) {
            throw new Error(
                `snapshot catch-up envelope missing data: ${JSON.stringify(body)}`,
            );
        }
        return body.data;
    };
}
